import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, MoreVertical, Tag, CalendarPlus } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, errMsg, imgUrl, money, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, ErrorState, StatusPill } from "@/components/States";
import { OfferCard } from "@/components/OfferCard";
import { AppointmentCard } from "@/components/AppointmentCard";
import { OfferDialog, PickupDialog, ReportDialog } from "@/components/DealDialogs";

const Bubble = ({ m, mine }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.type === "late" ? "bg-amber-100 text-amber-900" : mine ? "bg-blue-600 text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-900 rounded-bl-md"}`}>
      <p className="whitespace-pre-line break-words">{m.body}</p>
      <p className={`text-[10px] mt-1 ${mine && m.type !== "late" ? "text-blue-100" : "text-slate-400"}`}>{timeAgo(m.created_at)}</p>
    </div>
  </div>
);

const EventLine = ({ m }) => (
  <div className="flex justify-center"><span className="text-xs text-slate-500 bg-slate-100 rounded-full px-3 py-1 max-w-[90%] text-center">{m.body}</span></div>
);

const ThreadHeader = ({ data, reload }) => {
  const [report, setReport] = useState(false);
  const block = async () => {
    try {
      if (data.i_blocked) await api.delete(`/users/${data.other_user.id}/block`);
      else await api.post(`/users/${data.other_user.id}/block`);
      toast.success(data.i_blocked ? "User unblocked" : "User blocked. They can no longer message you.");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const l = data.listing;
  return (
    <div className="flex items-center gap-3 p-3 border-b border-slate-200 bg-white/95 backdrop-blur sticky top-16 md:top-0 z-10">
      <Link to="/inbox" data-testid="thread-back-btn" className="h-10 w-10 rounded-xl hover:bg-slate-100 flex items-center justify-center md:hidden"><ArrowLeft className="h-5 w-5" /></Link>
      <Link to={`/listing/${l.id}`} className="h-11 w-11 rounded-xl overflow-hidden bg-slate-100 shrink-0">{l.photos?.[0] && <img src={imgUrl(l.photos[0])} alt="" className="h-full w-full object-cover" />}</Link>
      <div className="min-w-0 flex-1">
        <Link to={`/listing/${l.id}`} data-testid="thread-listing-link" className="block truncate font-bold text-sm text-slate-900">{l.title} · {money(l.price)}</Link>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Link to={`/u/${data.other_user.id}`} className="hover:underline">{data.other_user.first_name}</Link> · {data.role === "buyer" ? "Seller" : "Buyer"} <StatusPill status={l.status} />
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger data-testid="thread-menu-btn" className="h-10 w-10 rounded-xl hover:bg-slate-100 flex items-center justify-center"><MoreVertical className="h-5 w-5" /></DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem data-testid="thread-block-btn" onClick={block}>{data.i_blocked ? "Unblock" : "Block"} {data.other_user.first_name}</DropdownMenuItem>
          <DropdownMenuItem data-testid="thread-report-btn" onClick={() => setReport(true)}>Report {data.other_user.first_name}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ReportDialog open={report} onOpenChange={setReport} targetType="user" targetId={data.other_user.id} />
    </div>
  );
};

export default function Thread() {
  const { id } = useParams();
  const { user, loadBadges } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [text, setText] = useState("");
  const [dlg, setDlg] = useState(null);
  const [schedOffer, setSchedOffer] = useState(null);
  const endRef = useRef();
  const count = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/conversations/${id}`);
      setData(d);
      loadBadges();
    } catch (e) {
      setErr(errMsg(e));
    }
  }, [id, loadBadges]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (data && data.messages.length !== count.current) {
      count.current = data.messages.length;
      endRef.current?.scrollIntoView({ block: "end" });
    }
  }, [data]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/conversations/${id}/messages`, { body: text });
      setText("");
      load();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };

  if (err) return <ErrorState message={err} onRetry={load} />;
  if (!data) return <Loading />;
  const lastIdx = {};
  data.messages.forEach((m, i) => m.ref_id && m.type !== "late" && (lastIdx[m.ref_id] = i));
  const l = data.listing;
  const hasOpenAppt = Object.values(data.appointments).some((a) => ["requested", "awaiting_buyer", "awaiting_seller", "confirmed"].includes(a.status));
  const acceptedOffer = Object.values(data.offers).find((o) => o.status === "accepted");

  return (
    <div className="md:max-w-3xl md:mx-auto md:border md:border-slate-200 md:rounded-2xl md:overflow-hidden bg-[#F4F5F7] flex flex-col min-h-[calc(100vh-6rem)] md:min-h-0 md:h-[calc(100vh-9rem)] -mx-4 md:mx-auto">
      <ThreadHeader data={data} reload={load} />
      <div data-testid="message-list" className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3">
        {data.messages.map((m, i) => {
          if (m.type === "offer" && data.offers[m.ref_id] && lastIdx[m.ref_id] === i) {
            return <div key={m.id} className={`flex ${m.sender_id === user.id ? "justify-end" : "justify-start"}`}><OfferCard offer={data.offers[m.ref_id]} role={data.role} onChange={load} onSchedule={hasOpenAppt ? null : (o) => { setSchedOffer(o); setDlg("pickup"); }} /></div>;
          }
          if (m.type === "appointment" && data.appointments[m.ref_id] && lastIdx[m.ref_id] === i) {
            return <div key={m.id} className="flex justify-center"><AppointmentCard appt={data.appointments[m.ref_id]} onChange={load} /></div>;
          }
          if (m.type === "text" || m.type === "late") return <Bubble key={m.id} m={m} mine={m.sender_id === user.id} />;
          return <EventLine key={m.id} m={m} />;
        })}
        <div ref={endRef} />
      </div>
      {data.role === "buyer" && l.status === "active" && !hasOpenAppt && !data.blocked && (
        <div className="flex gap-2 px-3 pt-2 overflow-x-auto">
          {l.price_type === "offers" && <button data-testid="thread-make-offer-btn" onClick={() => setDlg("offer")} className="shrink-0 h-9 px-3 rounded-full bg-white border border-slate-300 text-sm font-semibold flex items-center gap-1.5"><Tag className="h-4 w-4" /> Make offer</button>}
          <button data-testid="thread-schedule-btn" onClick={() => { setSchedOffer(acceptedOffer || null); setDlg("pickup"); }} className="shrink-0 h-9 px-3 rounded-full bg-white border border-slate-300 text-sm font-semibold flex items-center gap-1.5"><CalendarPlus className="h-4 w-4" /> Schedule pickup</button>
        </div>
      )}
      {data.blocked ? (
        <p data-testid="thread-blocked-notice" className="p-4 text-center text-sm text-slate-500 bg-white border-t">Messaging is unavailable because one of you blocked the other.</p>
      ) : (
        <form onSubmit={send} className="flex gap-2 p-3 bg-white border-t border-slate-200 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message…" maxLength={2000} className="flex-1 h-11 rounded-full border border-slate-300 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button data-testid="message-send-btn" disabled={!text.trim()} aria-label="Send" className="h-11 w-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center disabled:opacity-40"><Send className="h-4 w-4" /></button>
        </form>
      )}
      <OfferDialog open={dlg === "offer"} onOpenChange={(o) => setDlg(o ? "offer" : null)} listing={l} onDone={load} />
      {dlg === "pickup" && <PickupDialog open onOpenChange={(o) => setDlg(o ? "pickup" : null)} listing={l} offer={schedOffer} onDone={load} />}
    </div>
  );
}
